import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  Autocomplete,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Close as CloseIcon, 
  CheckCircle as SuccessIcon,
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { loadNavIdsFromDatabase } from '../../utils/NAV_ID_MAPPING';
import { PRODUCTS, GRADES, BRANDS, PACK_SIZES } from '../../utils/DROPDOWN_OPTIONS';

// ✅ Format pack size - always convert X to /
const formatPackSize = (packSize) => {
  if (!packSize) return '';
  return String(packSize).trim().replace(/X/gi, '/');
};

// ✅ Format all pack sizes from static options
const FORMATTED_PACK_SIZES = PACK_SIZES.map(size => formatPackSize(size));

// Ensure grades are stored as strings to prevent date parsing
const FORMATTED_GRADES = GRADES.map(grade => String(grade).trim());

function AddNavIdModal({ open, onClose, onSuccess }) {
  // Fixed fields - Product and Grade
  const [fixedData, setFixedData] = useState({
    product: '',
    grade: ''
  });

  // Current row being edited
  const [currentRow, setCurrentRow] = useState({
    pack_size: '',
    brand: '',
    nav_id: ''
  });

  // List of NAV IDs to add
  const [navIdRows, setNavIdRows] = useState([]);

  const [dropdownOptions, setDropdownOptions] = useState({
    products: PRODUCTS,
    grades: FORMATTED_GRADES,
    brands: BRANDS,
    pack_sizes: FORMATTED_PACK_SIZES
  });

  const [newOptionDialog, setNewOptionDialog] = useState({
    open: false,
    type: '',
    value: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [addingOption, setAddingOption] = useState(false);

  const highlightedValues = useRef({
    product: null,
    grade: null,
    pack_size: null,
    brand: null
  });

  useEffect(() => {
    if (open) {
      loadDropdowns();
    }
  }, [open]);

  const loadDropdowns = async () => {
    try {
      const types = ['products', 'grades', 'brands', 'pack_sizes'];
      const newOptions = { ...dropdownOptions };

      for (const type of types) {
        try {
          const response = await axios.get(`/api/dropdowns.php?type=${type}`, {
            withCredentials: true
          });
          if (response.data.success && response.data.data) {
            // Ensure all values are strings and format pack_sizes
            let data = (response.data.data || []).map(item => String(item).trim());
            
            // ✅ Format pack_sizes - convert X to /
            if (type === 'pack_sizes') {
              data = data.map(size => formatPackSize(size));
            }
            
            // Remove duplicates and empty values
            data = [...new Set(data)].filter(v => v);
            data.sort();
            
            newOptions[type] = data;
          }
        } catch (err) {
          console.error(`Error loading ${type}:`, err);
          // Keep existing options if API fails
        }
      }

      setDropdownOptions(newOptions);
    } catch (err) {
      console.error('Error loading dropdowns:', err);
    }
  };

  // ✅ Get placeholder text based on field type
  const getPlaceholderText = (type) => {
    const placeholders = {
      product: 'e.g., CEZP',
      grade: 'e.g., 31/40',
      brand: 'e.g., GOLD STAR',
      pack_size: 'e.g., 10/2 LB'
    };
    return placeholders[type] || '';
  };

  // ✅ Get dialog title based on field type
  const getDialogTitle = (type) => {
    const titles = {
      product: 'Add New Product',
      grade: 'Add New Grade',
      brand: 'Add New Brand',
      pack_size: 'Add New Pack Size'
    };
    return titles[type] || 'Add New Option';
  };

  const handleAddNewOption = async () => {
    if (!newOptionDialog.type || !newOptionDialog.value) {
      alert('Please enter a value');
      return;
    }

    setAddingOption(true);
    setError('');

    try {
      // ✅ Format pack_size before sending to API
      let valueToSend = String(newOptionDialog.value).trim();
      if (newOptionDialog.type === 'pack_size') {
        valueToSend = formatPackSize(valueToSend);
      }

      const response = await axios.post(
        `/api/dropdowns.php?type=${newOptionDialog.type}`,
        {
          name: valueToSend
        },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );

      if (response.data.success) {
        const newOptions = { ...dropdownOptions };
        const typeKey = newOptionDialog.type === 'pack_size' ? 'pack_sizes' : newOptionDialog.type + 's';
        const newValue = response.data.name || valueToSend;
        
        if (!newOptions[typeKey].includes(newValue)) {
          newOptions[typeKey] = [...newOptions[typeKey], newValue].sort();
          setDropdownOptions(newOptions);
        }

        setNewOptionDialog({ open: false, type: '', value: '' });
        setError('');
        setSuccess(`✅ ${newValue} added successfully!`);
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(`❌ ${response.data.error || 'Failed to add option'}`);
      }
    } catch (err) {
      console.error('Error adding option:', err);
      setError(`❌ ${err.response?.data?.error || err.message || 'Failed to add option'}`);
    } finally {
      setAddingOption(false);
    }
  };

  // Add current row to table
  const handleAddRow = () => {
    setError('');

    if (!currentRow.pack_size) {
      setError('❌ Please fill in Pack Size');
      return;
    }
    if (!currentRow.brand) {
      setError('❌ Please fill in Brand');
      return;
    }
    if (!currentRow.nav_id) {
      setError('❌ Please fill in NAV ID');
      return;
    }

    // ✅ Format pack_size before adding
    const formattedPackSize = formatPackSize(currentRow.pack_size);

    // Check for duplicates
    const duplicate = navIdRows.find(
      row => row.pack_size === formattedPackSize && row.brand === currentRow.brand
    );

    if (duplicate) {
      setError(`❌ This Pack Size + Brand combination already exists`);
      return;
    }

    // Add to list with unique ID
    const newRow = {
      id: Date.now(),
      pack_size: formattedPackSize,
      brand: String(currentRow.brand).trim(),
      nav_id: String(currentRow.nav_id).trim()
    };

    setNavIdRows([...navIdRows, newRow]);
    setCurrentRow({
      pack_size: '',
      brand: '',
      nav_id: ''
    });
    setSuccess(`✅ Row added! Add more or submit.`);
    setTimeout(() => setSuccess(''), 2000);
  };

  // Remove row from table
  const handleRemoveRow = (id) => {
    setNavIdRows(navIdRows.filter(row => row.id !== id));
    setError('');
  };

  // Submit all rows
  const handleSubmitAll = async () => {
    setError('');
    setSuccess('');

    if (!fixedData.product) {
      setError('❌ Please select Product');
      return;
    }
    if (!fixedData.grade) {
      setError('❌ Please select Grade');
      return;
    }
    if (navIdRows.length === 0) {
      setError('❌ Please add at least one Pack Size + Brand combination');
      return;
    }

    setSubmitting(true);

    try {
      // Submit all rows
      let successCount = 0;
      let failCount = 0;

      for (const row of navIdRows) {
        try {
          const response = await axios.post(
            '/api/nav_ids.php',
            {
              product: String(fixedData.product).trim(),
              grade: String(fixedData.grade).trim(),
              pack_size: formatPackSize(String(row.pack_size).trim()),
              brand: String(row.brand).trim(),
              nav_id: String(row.nav_id).trim()
            },
            {
              headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              withCredentials: true,
              timeout: 10000
            }
          );

          if (response.data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }

      // Reload NAV IDs
      try {
        await loadNavIdsFromDatabase();
      } catch (loadError) {
        console.error('Error reloading:', loadError);
      }

      // Show result
      if (failCount === 0) {
        setSuccess(`✅ All ${successCount} NAV IDs added successfully!`);
        if (onSuccess) {
          onSuccess();
        }
        
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setSuccess(`✅ Added: ${successCount}, Failed: ${failCount}`);
      }
    } catch (err) {
      setError(`❌ Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting || addingOption) return;

    setFixedData({
      product: '',
      grade: ''
    });
    setCurrentRow({
      pack_size: '',
      brand: '',
      nav_id: ''
    });
    setNavIdRows([]);
    setSuccess('');
    setError('');
    setLoading(false);
    setNewOptionDialog({ open: false, type: '', value: '' });
    highlightedValues.current = {
      product: null,
      grade: null,
      pack_size: null,
      brand: null
    };
    onClose();
  };

  const handleFieldChange = (section, field, value) => {
    // ✅ Format pack_size on change
    let processedValue = value || '';
    if (field === 'pack_size' && processedValue) {
      processedValue = formatPackSize(processedValue);
    }
    
    if (section === 'fixed') {
      setFixedData(prev => ({ ...prev, [field]: processedValue }));
    } else if (section === 'current') {
      setCurrentRow(prev => ({ ...prev, [field]: processedValue }));
    }
    if (error) setError('');
  };

  const handleKeyDown = (event, field) => {
    if (event.key === 'Tab' && highlightedValues.current[field]) {
      event.preventDefault();
      handleFieldChange('fixed', field, highlightedValues.current[field]);
      highlightedValues.current[field] = null;
    }
  };

  const handleHighlightChange = (field, option) => {
    highlightedValues.current[field] = option;
  };

  const renderDropdownWithAddButton = (section, field, label, options, placeholder) => {
    const typeMap = {
      product: 'product',
      grade: 'grade',
      brand: 'brand',
      pack_size: 'pack_size'
    };

    const data = section === 'fixed' ? fixedData : currentRow;

    // ✅ Format pack_sizes in options display
    const displayOptions = field === 'pack_size' 
      ? options.map(opt => formatPackSize(String(opt).trim()))
      : options.map(opt => String(opt).trim());
    
    // Remove duplicates
    const uniqueOptions = [...new Set(displayOptions)].filter(v => v);

    return (
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Autocomplete
            freeSolo
            autoHighlight
            selectOnFocus
            clearOnBlur={false}
            handleHomeEndKeys
            options={uniqueOptions}
            value={data[field]}
            onChange={(e, newValue) => handleFieldChange(section, field, newValue)}
            onInputChange={(e, newInputValue) => {
              if (e && e.type === 'change') {
                handleFieldChange(section, field, newInputValue);
              }
            }}
            onHighlightChange={(e, option) => handleHighlightChange(field, option)}
            disabled={loading || submitting}
            sx={{ flex: 1, minWidth: 200 }}
            filterOptions={(opts, state) => {
              // Filter without date parsing
              const inputValue = String(state.inputValue || '').toLowerCase();
              return opts.filter(option =>
                String(option).toLowerCase().includes(inputValue)
              );
            }}
            noOptionsText={`No ${label.toLowerCase()} found`}
            ListboxProps={{
              style: { maxHeight: '300px' }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                required
                label={label}
                placeholder={placeholder}
                size="small"
                type="text"
                onKeyDown={(e) => handleKeyDown(e, field)}
              />
            )}
          />
          <Tooltip title={`Add new ${label.toLowerCase()}`}>
            <IconButton
              color="primary"
              onClick={() => setNewOptionDialog({ 
                open: true, 
                type: typeMap[field], 
                value: '' 
              })}
              disabled={loading || submitting}
              sx={{ mt: 0.5 }}
            >
              <AddCircleIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
    );
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: '900px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600, fontSize: '1.3rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1 }} />
            Add Multiple NAV ID Mappings
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, pt: 3, pb: 3 }}>
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 2 }}
              icon={<SuccessIcon />}
            >
              {success}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* SECTION 1: Fixed Product & Grade */}
          <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              1. Select Product & Grade (Fixed for all mappings below)
            </Typography>
            <Grid container spacing={2}>
              {renderDropdownWithAddButton('fixed', 'product', 'Product *', dropdownOptions.products, 'e.g., CEZP')}
              {renderDropdownWithAddButton('fixed', 'grade', 'Grade *', dropdownOptions.grades, 'e.g., 31/40')}
            </Grid>
          </Box>

          {/* SECTION 2: Add multiple Pack Size + Brand + NAV ID rows */}
          <Box sx={{ mb: 4, p: 2, bgcolor: '#f0f8ff', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              2. Add Pack Size + Brand + NAV ID Combinations (Click "Add Row" for each)
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {renderDropdownWithAddButton('current', 'pack_size', 'Pack Size *', dropdownOptions.pack_sizes, 'e.g., 10/2 LB')}
              {renderDropdownWithAddButton('current', 'brand', 'Brand *', dropdownOptions.brands, 'e.g., GOLD STAR')}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="NAV ID *"
                  value={currentRow.nav_id}
                  onChange={(e) => handleFieldChange('current', 'nav_id', e.target.value)}
                  disabled={submitting}
                  placeholder="e.g., BPSFG-CEZP-001"
                  size="small"
                  type="text"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddRow}
                  disabled={submitting || !currentRow.pack_size || !currentRow.brand || !currentRow.nav_id}
                  sx={{ height: '40px', fontWeight: 600 }}
                >
                  Add Row
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* SECTION 3: Table of added rows */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              3. Review Added Mappings ({navIdRows.length} rows)
            </Typography>
            {navIdRows.length === 0 ? (
              <Alert severity="info">
                No rows added yet. Add rows using the form above.
              </Alert>
            ) : (
              <TableContainer component={Paper} elevation={2}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1565c0' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Grade</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Pack Size</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Brand</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>NAV ID</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {navIdRows.map((row, index) => (
                      <TableRow key={row.id} sx={{ bgcolor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                        <TableCell sx={{ fontWeight: 600 }}>{fixedData.product}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{fixedData.grade}</TableCell>
                        <TableCell>{row.pack_size}</TableCell>
                        <TableCell>{row.brand}</TableCell>
                        <TableCell>
                          <Chip 
                            label={row.nav_id} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete this row">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveRow(row.id)}
                              disabled={submitting}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end', bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleClose} 
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitAll} 
            variant="contained" 
            color="success"
            disabled={submitting || navIdRows.length === 0 || !fixedData.product || !fixedData.grade}
            startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{ minWidth: 150 }}
          >
            {submitting ? `Saving ${navIdRows.length}...` : `Save All ${navIdRows.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ FIXED: Add New Option Dialog with correct placeholders */}
      <Dialog 
        open={newOptionDialog.open}
        onClose={() => !addingOption && setNewOptionDialog({ open: false, type: '', value: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', fontWeight: 600 }}>
          {getDialogTitle(newOptionDialog.type)}
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, pt: 3 }}>
          <TextField
            fullWidth
            required
            autoFocus
            label={newOptionDialog.type === 'pack_size' ? 'Pack Size' : 
                   newOptionDialog.type === 'product' ? 'Product' :
                   newOptionDialog.type === 'grade' ? 'Grade' :
                   newOptionDialog.type === 'brand' ? 'Brand' : 'Value'}
            value={newOptionDialog.value}
            onChange={(e) => setNewOptionDialog(prev => ({ ...prev, value: e.target.value }))}
            disabled={addingOption}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddNewOption();
              }
            }}
            size="small"
            type="text"
            placeholder={getPlaceholderText(newOptionDialog.type)}
            helperText={newOptionDialog.type === 'pack_size' ? 
              'Use format like: 10/2 LB, 6/5 LB, 16/12 OZ' : 
              newOptionDialog.type === 'grade' ?
              'Use format like: 31/40, 21/25, 16/20' : ''}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button 
            onClick={() => setNewOptionDialog({ open: false, type: '', value: '' })}
            disabled={addingOption}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddNewOption}
            variant="contained"
            color="info"
            disabled={addingOption || !newOptionDialog.value}
          >
            {addingOption ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AddNavIdModal;